import axiosInstance from '../../helpers/bearerAxios';
import axios from 'axios';
import { logger } from '../loggingService';

const contractBaseUrl =
    import.meta.env.VITE_ONEBUTTON_CONTRACT_BASE_URL ||
    (import.meta.env.VITE_ONEBUTTON_CONTRACT_API_URL || '').replace(/\/api\/Contract.*$/i, '') ||
    'https://apionebuttoncontractdev.azurewebsites.net';

const masterBaseUrl =
    import.meta.env.VITE_ONEBUTTON_MASTER_API_URL ||
    'https://apionebuttonmasterdatadev.azurewebsites.net/api/MasterData';

const boxRPAApi = import.meta.env.VITE_BOX_RPA_API || 'https://trinity-rpa-dev.trinity-solar.com';
const boxEmbedUrl = import.meta.env.VITE_BOX_EMBED_URL || 'https://trinity-rpa-dev.trinity-solar.com/boxSharedLink/';
const boxToken =
    import.meta.env.VITE_BOX_TOKEN ||
    'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJ1c2VyX2lkIjoiUlBBX1VTRVIiLCJ1c2VybmFtZSI6Ik9uZUJ1dHRvbiJ9.-26USso15xzROO8RQkcOBcKReaFCgBtoSnvXoyi9iNc';

export interface DocumentItem {
    DocumentId?: number | string;
    FileName?: string;
    FilePath?: string;
    Category?: string;
    CatogoryId?: number | string;
    Comments?: string;
    LastUpdatedOn?: string;
    ProjectBlockId?: string;
    ProjectBlockName?: string;
    UploadToSunnova?: boolean;
}

export interface ContractStatusDetails {
    FinancePartner?: string;
    ContractType?: string;
    ContractId?: string;
    ContractExpiration?: string;
    InstallationDate?: string | null;
    PropertyOwnerSignature?: boolean | string | null;
    CreditPasserSignature?: boolean | string | null;
    creditPasserSignature?: boolean | string | null;
    ACHVerification?: boolean | string | null;
    ContractValidation?: boolean | string | null;
    IsContractSigned?: boolean;
}

export interface ProjectBlockItem {
    BlockId: string | number;
    BlockName: string;
}

export interface RetrieveDocumentsResponse {
    SalesForce_Id?: string;
    Opportunity_Id?: number | string;
    box__folder_id__c?: string;
    ContractStatus?: ContractStatusDetails | null;
    ProjectBlocks?: ProjectBlockItem[];
    DocumentList?: DocumentItem[];
    [key: string]: any;
}

export interface SaveContractStatusPayload {
    Opportunity_Id: number | string;
    SalesForce_Id?: string;
    box__folder_id__c?: string;
    ProjectBlocks?: ProjectBlockItem[];
    ContractStatus: ContractStatusDetails;
}

export interface CategoryOption {
    Value: string | number;
    Text: string;
}

/**
 * Retrieves dropdown master data for step 5 (Documents Category).
 */
export const getMasterDataByStep = async (step: number = 5): Promise<{ Category: CategoryOption[] }> => {
    try {
        const url = `${masterBaseUrl}/GetMasterDataByStep`;
        const response = await axiosInstance.post(url, {
            step,
            paramDTOs: [{ key: 'string', value: 'string' }],
        });
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Retrieves the list of documents and contract status for an opportunity.
 */
export const getDocuments = async (
    opportunityId: string | number
): Promise<RetrieveDocumentsResponse> => {
    try {
        const url = `${contractBaseUrl}/api/Contract/retrieveDocuments?opportunityId=${encodeURIComponent(String(opportunityId))}`;
        const response = await axiosInstance.get(url);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Saves contract status details.
 */
export const saveContractStatus = async (
    data: SaveContractStatusPayload
): Promise<{ Message?: string; data?: string; [key: string]: any }> => {
    try {
        const url = `${contractBaseUrl}/api/Contract/SaveContractStatus`;
        const response = await axiosInstance.post(url, data);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Uploads a new document with FormData.
 */
export const uploadDocuments = async (
    formData: FormData
): Promise<{ data?: string; Message?: string; [key: string]: any }> => {
    try {
        const url = `${contractBaseUrl}/api/Contract/UploadDocuments`;
        const response = await axiosInstance.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Retrieves Box files iframe details by opportunity ID.
 */
export const getBoxFilesDetails = async (
    opportunityId: string | number
): Promise<any> => {
    try {
        const url = `${contractBaseUrl}/api/Contract/GetBoxFilesDetails?OpportunityId=${encodeURIComponent(String(opportunityId))}`;
        const response = await axiosInstance.get(url);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Retrieves Box folder ID by Salesforce ID.
 */
export const getBoxFolderId = async (
    salesforceId: string
): Promise<{ folderID?: string; folderId?: string; [key: string]: any }> => {
    try {
        const url = `${boxRPAApi}/api/box/folderid/${encodeURIComponent(salesforceId)}/app/OneButton`;
        const response = await axios.get(url, {
            headers: {
                token: boxToken,
                'x-frame-options': 'SAMEORIGIN',
            },
        });
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Retrieves Box embed folder URL.
 */
export const getEmbedFolderUrl = async (
    folderId: string,
    userEmail?: string
): Promise<{ embed_link?: string; [key: string]: any }> => {
    try {
        const email = userEmail || (typeof window !== 'undefined' ? localStorage.getItem('userEmail') || 'user' : 'user');
        const url = `${boxEmbedUrl}${encodeURIComponent(folderId)}/folder/${encodeURIComponent(email)}/app/OneButton`;
        const response = await axios.get(url, {
            headers: {
                token: boxToken,
                'x-frame-options': 'SAMEORIGIN',
            },
        });
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

const documentsService = {
    getMasterDataByStep,
    getDocuments,
    saveContractStatus,
    uploadDocuments,
    getBoxFilesDetails,
    getBoxFolderId,
    getEmbedFolderUrl,
};

export default documentsService;

